<p-dialog
  [visible]="visible"
  (visibleChange)="visibleChange.emit($event)"
  [modal]="true"
  [dismissableMask]="true"
  [closable]="false"
  [closeOnEscape]="true"
  [showHeader]="false"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '90vw', maxWidth: '700px', height: '90vh', maxHeight: '800px' }"
  [contentStyle]="{ padding: '0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }"
  styleClass="project-detail-modal"
>
  @if (project) {
    <!-- Image Carousel Header -->
    <div class="relative h-56 flex-shrink-0 overflow-hidden bg-gradient-to-br from-primary-100 to-primary-50">
      @if (getProjectImages().length > 0) {
        <!-- Current image with slide animation -->
        <img
          [src]="getCurrentImage()"
          [alt]="getProjectName()"
          class="h-full w-full object-cover"
          [class.animate-slide-from-right]="slideDirection() === 'right'"
          [class.animate-slide-from-left]="slideDirection() === 'left'"
        />
        <!-- Gradient overlay - soft green pastel -->
        <div class="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary-900/25 to-transparent"></div>
      } @else {
        <!-- No image: logo backdrop on light pastel background -->
        <div class="flex h-full w-full items-center justify-center">
          <img
            src="/assets/Logo___Greentripper_primary_horizontal_with_slogan.svg"
            alt="Greentripper"
            class="h-16 opacity-60"
          />
        </div>
      }

      <!-- Close button - top right -->
      <button
        class="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/80 shadow-md transition-all hover:bg-white hover:shadow-lg hover:scale-105"
        (click)="onClose()"
        type="button"
      >
        <lucide-angular [img]="xIcon" class="h-5 w-5 text-gray-700" />
      </button>

      <!-- Carousel controls (if multiple images) -->
      @if (getProjectImages().length > 1) {
        <!-- Previous button -->
        <button
          class="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/20 text-white transition-all hover:bg-black/40"
          (click)="prevImage(); $event.stopPropagation()"
          type="button"
        >
          <lucide-angular [img]="chevronLeftIcon" class="h-5 w-5" />
        </button>

        <!-- Next button -->
        <button
          class="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/20 text-white transition-all hover:bg-black/40"
          (click)="nextImage(); $event.stopPropagation()"
          type="button"
        >
          <lucide-angular [img]="chevronRightIcon" class="h-5 w-5" />
        </button>

        <!-- Dots indicator -->
        <div class="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
          @for (img of getProjectImages(); track $index) {
            <button
              class="h-2 w-2 rounded-full transition-colors"
              [class]="$index === currentImageIndex() ? 'bg-white' : 'bg-white/50'"
              (click)="goToImage($index); $event.stopPropagation()"
              type="button"
              [attr.aria-label]="'Go to image ' + ($index + 1)"
            ></button>
          }
        </div>
      }
    </div>

    <!-- Header: 2-column layout -->
    <div class="flex flex-shrink-0 items-start justify-between border-b border-gray-200 px-6 py-4">
      <!-- Left column: Tags + Location info -->
      <div class="flex flex-col gap-1.5">
        <!-- Tags -->
        <div class="flex flex-wrap gap-2">
          @if (project.isCertifiedOffset) {
            <span class="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <lucide-angular [img]="shieldIcon" class="h-3 w-3" />
              {{ getCertificationLabel() }}
            </span>
          }
          <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
            {{ getProjectTypeLabel() }}
          </span>
        </div>
        <!-- Location + Certification + Status - icônes + texte avec séparateurs -->
        <div class="flex items-center text-xs text-gray-500">
          <lucide-angular [img]="mapPinIcon" class="mr-1 h-3 w-3" />
          {{ getLocationDisplay() }}
          @if (project.isCertifiedOffset && project.certificationYear) {
            <span class="mx-1.5">·</span>
            <lucide-angular [img]="awardIcon" class="mr-1 h-3 w-3" />
            {{ 'contribute.projectDetails.certifiedSince' | transloco }} {{ project.certificationYear }}
          }
          <span class="mx-1.5">·</span>
          <lucide-angular [img]="checkCircleIcon" class="mr-1 h-3 w-3 text-validation-600" />
          <span class="text-validation-600">{{ 'contribute.projectDetails.statusActive' | transloco }}</span>
        </div>
      </div>

      <!-- Right column: Price -->
      <div class="flex flex-col items-end gap-0.5">
        <p class="text-2xl font-bold text-primary-600">{{ project.pricePerTonne | number:'1.2-2' }} EUR</p>
        <p class="text-xs text-gray-500">{{ 'contribute.projectDetails.perTonne' | transloco }}</p>
      </div>
    </div>

    <!-- Scrollable Content Area -->
    <div class="flex-1 space-y-6 overflow-y-auto px-6 py-5">
      <!-- Title + Short description -->
      <div>
        <h2 class="mb-2 text-2xl font-bold text-gray-900">{{ getProjectName() }}</h2>
        <p class="text-gray-600">{{ getShortDescription() }}</p>
      </div>

      <!-- Project Overview -->
      <div>
        <h3 class="mb-3 text-lg font-semibold text-gray-900">
          {{ 'contribute.projectDetails.overview' | transloco }}
        </h3>
        <p class="whitespace-pre-line text-gray-600">{{ getLongDescription() }}</p>
      </div>

      <!-- Impact Section -->
      @if (hasImpactMetrics() || (project.coBenefits && project.coBenefits.length > 0)) {
        <div class="space-y-4">

          <!-- KPIs - Métriques chiffrées -->
          @if (hasImpactMetrics()) {
            <div>
              <h3 class="mb-3 text-lg font-semibold text-gray-900">
                {{ 'contribute.projectDetails.impact' | transloco }}
              </h3>
              <div class="grid grid-cols-2 gap-3">
                @for (metric of getImpactMetrics(); track metric.label) {
                  <div class="rounded-lg border border-gray-200 bg-white p-4 text-center">
                    <div class="mb-2 flex justify-center">
                      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                        <lucide-angular [img]="metric.icon" class="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <p class="text-xl font-bold text-gray-900">{{ metric.value }}</p>
                    <p class="text-xs text-gray-500">{{ metric.label }}</p>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Co-benefits - Tags inline -->
          @if (project.coBenefits && project.coBenefits.length > 0) {
            <div>
              @if (!hasImpactMetrics()) {
                <h3 class="mb-3 text-lg font-semibold text-gray-900">
                  {{ 'contribute.projectDetails.impact' | transloco }}
                </h3>
              }
              <p class="mb-2 text-sm font-medium text-gray-500">Co-bénéfices</p>
              <div class="flex flex-wrap gap-2">
                @for (benefit of project.coBenefits; track benefit) {
                  <span class="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-sm text-primary-700">
                    <lucide-angular [img]="getCoBenefitIcon(benefit)" class="h-3.5 w-3.5" />
                    {{ getCoBenefitLabel(benefit) }}
                  </span>
                }
              </div>
            </div>
          }

        </div>
      }
    </div>

    <!-- Sticky Footer -->
    <div class="flex flex-shrink-0 items-center justify-end border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
      <button
        pButton
        icon="pi pi-check"
        (click)="onSelect()"
      >
        {{ 'contribute.projectDetails.selectProject' | transloco }}
      </button>
    </div>
  }
</p-dialog>
